import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useRouter } from 'next/router'
import { productListViewed } from '@helpers/products'
import CollectionDetails from '@components/collection/CollectionDetails'
import Hero from './Hero'
import CollectionWrapper from './Collection/index'
import CollectionContext from './collectionContext'
import styles from './styles.module.scss'
import type {
  Collection,
  CollectionConfig,
  CollectionListMeta
} from 'types/collection'
import CollectionFilter from './CollectionFilter'
import { FilterBuild } from './CollectionFilter/FiltersSidePanel/types'
import { FilterOption } from 'types/filters'
import { useFetchVariants } from './hooks/useFetchVariants'
import { useFilterContext } from './hooks/useFilterContext'
import FilterToggleButton from './CollectionFilter/FiltersSidePanel/FilterToggleButton'
import { Fit } from 'types/fit'
import { BUILD } from '@consts/index'
import getCollectionProductData from '@helpers/filters/getCollectionProductData'
import CustomHead from '@components/Head'
import { CollectionProductFamilyFilter } from './CollectionProductFamilyFilter'
import {
  filterByProductFeed,
  getAvailableProductStyles,
  getEcomProductCategoriesByType
} from './helpers'
import { useFit } from '@helpers/hooks/useFit'

const MAX_NUMBER_OF_ECOM_CATEGORY_HEADER = 2 // 1 + 'All' option

function getLimitForEcomHeaderCategories(collectionHandle: string) {
  if (
    collectionHandle &&
    EXCLUDED_ECOM_HEADERS.includes(collectionHandle.toLowerCase().trim())
  ) {
    return MAX_NUMBER_OF_ECOM_CATEGORY_HEADER + 1
  }

  return MAX_NUMBER_OF_ECOM_CATEGORY_HEADER
}

const EXCLUDED_ECOM_HEADERS = ['denim']

function getEcomProductFilters(
  collections: Collection[],
  collectionHandle: string
) {
  const ecomProductsByHeader = getEcomProductCategoriesByType(
    collections,
    'ecom_product_header'
  )
  const ecomProductsByGrouping = getEcomProductCategoriesByType(
    collections,
    'ecom_product_grouping'
  )
  const maxLimitEcomHeaderCategories =
    getLimitForEcomHeaderCategories(collectionHandle)

  if (ecomProductsByHeader.length > maxLimitEcomHeaderCategories) {
    return {
      values: ecomProductsByHeader,
      type: 'header'
    }
  }

  if (
    ecomProductsByHeader.length === maxLimitEcomHeaderCategories &&
    ecomProductsByGrouping.length > 2 &&
    !EXCLUDED_ECOM_HEADERS.includes(collectionHandle)
  ) {
    return {
      values: ecomProductsByGrouping,
      type: 'group'
    }
  }

  return {
    values: getAvailableProductStyles(collections),
    type: 'style'
  }
}

function getCollectionProductsCount({
  collections,
  filterOptions,
  variantExist
}: {
  collections: Collection[]
  filterOptions: FilterOption[]
  variantExist: boolean
}) {
  const { collectionsWithFilteredProducts } = getCollectionProductData({
    collections,
    filterOptions,
    variantExist
  })
  return collectionsWithFilteredProducts.reduce((total, collection) => {
    return total + collection.items.length
  }, 0)
}

type CollectionListProps = {
  collectionHandle: Collection['handle']
  collectionData: {
    title: string
    byline: string
    description: string
    collections: Collection[]
    collectionConfig?: CollectionConfig
    meta: CollectionListMeta
  }
  filterBuild: FilterBuild
}

function CollectionList({
  collectionHandle,
  collectionData,
  filterBuild
}: CollectionListProps) {
  const router = useRouter()
  const {
    filters,
    storeFilters,
    isSavedFitApplied,
    storeSavedFitApplied,
    savedFit
  } = useFit()

  const { title, byline, collections, meta, description } = collectionData
  const { style: queryProductStyle } = router.query as {
    category: string
    collectonHandle: string
    style: string
  }

  const [selectedProductStyle, setSelectedProductStyle] =
    useState<string>('All')

  const { updatedCollections, variantExist, inventoryReady } = useFetchVariants(
    {
      collectionHandle,
      collections // passes SSG Collection data
    }
  )

  const { isFiltersOpen, closeFilters, openFilters, showFilters } =
    useFilterContext({
      metaFilters: meta.filters
    })

  const [isFitFilterToggleActive, setIsFitFilterToggleActive] =
    useState<boolean>(isSavedFitApplied)

  useEffect(() => {
    setIsFitFilterToggleActive(isSavedFitApplied)
  }, [isSavedFitApplied])

  const filterOptions = useMemo(() => filters || [], [filters])

  const { values: ecomProductFilters, type: ecomProductFiltersType } =
    getEcomProductFilters(updatedCollections, collectionHandle)

  const isCollectionEcomCategoriesFilterVisible = ecomProductFilters.length > 2

  const filteredCollections = useMemo(() => {
    let collectionsData = updatedCollections

    if (selectedProductStyle !== 'All') {
      collectionsData = filterByProductFeed(
        updatedCollections,
        selectedProductStyle,
        ecomProductFiltersType
      )
    }

    return collectionsData
  }, [selectedProductStyle, updatedCollections, ecomProductFiltersType])

  const totalResults = useMemo(() => {
    return getCollectionProductsCount({
      collections: filteredCollections,
      filterOptions,
      variantExist: inventoryReady && variantExist
    })
  }, [filteredCollections, filterOptions, inventoryReady, variantExist])

  useEffect(() => {
    const productStyle = ecomProductFilters.includes(queryProductStyle)
      ? queryProductStyle
      : 'All'

    setSelectedProductStyle(productStyle)
  }, [queryProductStyle])

  const handleProductFamilyClick = (filter: string) => {
    if (filter === 'All') {
      delete router.query.style
    } else {
      router.query.style = filter
    }

    router.push(router, undefined, { shallow: true })
  }

  useEffect(() => {
    if (!inventoryReady) return

    const eventProducts = filteredCollections.flatMap(collectionEntry => {
      return collectionEntry.items.map((product, index) => ({
        quantity: 1,
        name: collectionEntry.title,
        color: product.title,
        price: parseFloat(product.price),
        position: index + 1,
        url: product.handle,
        image_url: product.images[0]?.src,
        brand: 'SPOKE',
        product_id: product.product_feed?.product_id
      }))
    })
    productListViewed({
      context: 'Collection',
      listId: collectionHandle,
      listName: title,
      products: eventProducts,
      savedFit
    })
  }, [inventoryReady, filteredCollections, collectionHandle, title])

  const collectionContext = useMemo(
    () => ({
      collectionHandle,
      hasMultipleProductTypes: filteredCollections?.length > 1,
      collectionMainTitle: title,
      closeFilters,
      openFilters,
      isFiltersOpen,
      isFitFilterToggleActive,
      setIsFitFilterToggleActive
    }),
    [isFiltersOpen, isFitFilterToggleActive]
  )

  const handleFilterClear = () => {
    setIsFitFilterToggleActive(false)
  }

  const handleFilterApply = useCallback(
    (options: FilterOption[]) => {
      const isSavedFitValid = !!(
        savedFit &&
        options.every(
          option =>
            savedFit[option.type as keyof Fit] ===
            String(option.value.replace(BUILD, '').trim())
        )
      )

      storeSavedFitApplied(isSavedFitValid && isFitFilterToggleActive)
      storeFilters(options)
      closeFilters()
    },
    [
      closeFilters,
      isFitFilterToggleActive,
      savedFit,
      storeSavedFitApplied,
      storeFilters
    ]
  )

  const handleApplySavedFitClick = useCallback((state: boolean) => {
    setIsFitFilterToggleActive(state)
  }, [])

  return (
    <>
      <CustomHead
        includeNoFollow={meta.page_crawl}
        title={meta.seo_title}
        desc={meta.seo_description}
        image={meta.seo_image}
      />
      <div style={{ minHeight: 800 }}>
        <CollectionContext.Provider value={collectionContext}>
          <section className={styles.CollectionHeader}>
            <Hero
              title={
                selectedProductStyle === 'All' ? title : selectedProductStyle
              }
              parentTitle={title}
              parentTitleVisible={isCollectionEcomCategoriesFilterVisible}
              byline={byline}
            />

            {isCollectionEcomCategoriesFilterVisible && (
              <CollectionProductFamilyFilter
                filters={ecomProductFilters}
                selectedFilter={selectedProductStyle}
                selectProductFamilyFilter={handleProductFamilyClick}
              />
            )}
          </section>

          <FilterToggleButton
            totalResults={totalResults}
            filterBuild={filterBuild}
            isApplyFitActive={isSavedFitApplied}
          />

          <section className={styles.collectionContainer}>
            <div className={styles.collectionGridContainer}>
              {showFilters && (
                <div className={styles.collectionFilterWrapper}>
                  <CollectionFilter
                    filterOptions={filterOptions}
                    filterBuild={filterBuild}
                    onFilterClear={handleFilterClear}
                    onFilterApply={handleFilterApply}
                    onApplyFitToggle={handleApplySavedFitClick}
                  />
                </div>
              )}
              <div className={styles.collectionWrapper}>
                <CollectionWrapper
                  collections={filteredCollections}
                  filterOptions={filterOptions}
                  variantExist={variantExist}
                  inventoryReady={inventoryReady}
                />
              </div>
            </div>
          </section>

          {description && (
            <div className={styles.collection}>
              <CollectionDetails copy={description} />
            </div>
          )}
        </CollectionContext.Provider>
      </div>
    </>
  )
}

export default CollectionList
